import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import Layout from 'components/layout';
import Head from 'components/head';
import { Box, Heading, Text, Flex, List, ListItem, ListIcon } from '@chakra-ui/react';
import HeroSection from '../../components/heroSection';
import { graphql, useStaticQuery } from 'gatsby';
import Proyectos from '../../components/proyectos';
import ProyectoCard from '../../components/proyectoCard';
import { VideoSchema } from '../../helpers/schemaGenerator';
import ContactBanner from '../../components/contactBanner';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { StaticImage } from 'gatsby-plugin-image';
// import ogImg from '../../../content/images/servicios/VideoMapping_3d-Mexico.jpg';

const Mapping = ({ lang='es' }) => {
  const l = (...texts) => lang === 'en' ? texts[1] : texts[0]
  const data = useStaticQuery(
    graphql`
      query MappingQuery {
        headerImg: file(
          relativePath: {eq: "images/servicios/VideoMapping_3d-Mexico.jpg"}
        ) {
          childImageSharp {
            gatsbyImageData(quality: 80, layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
            resize(width: 1200, height: 630, quality: 80, cropFocus: ENTROPY, fit: INSIDE) {
              src
            }
          }
        }
        headerImgMovil: file(
          relativePath: {eq: "images/servicios/VideoMapping_3d_Mexico-M.jpg"}
        ) {
          childImageSharp {
            gatsbyImageData(width: 600, quality: 80, layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
          }
        }
        proyectoCharo: mdx(fields: { slug: { eq: "/mapping-3d-charo-michoacan" } }) {
        ...ProyectoCardData
        }
        proyectoCholula: mdx(fields: { slug: { eq: "/mapping-museo-regional-de-cholula" } }) {
            ...ProyectoCardData
        }
        proyectoBalon: mdx(
          fields: { slug: { eq: "/3d-mapping-balon-aspid-limpiemos-nuestro-mexico" } }
        ) {
            ...ProyectoCardData
        }
        proyectoXx: mdx(
          fields: { slug: { eq: "/mapping-3d-xx-festival-internacional-de-cine-de-morelia" } }
        ) {
            ...ProyectoCardData
        }
        proyectoQuinzino: mdx(
          fields: { slug: { eq: "/3d-mapping-bticino-ciudad-quinzino" } }
        ) {
            ...ProyectoCardData
        }
        proyectoMuseoMusica: mdx(
          fields: { slug: { eq: "/mapping-3d-360-museo-de-la-musica-de-puebla" } }
        ) {
            ...ProyectoCardData
        }
        proyectoEpura: mdx(
          fields: { slug: { eq: "/epura-espectaculo-de-mapping-4d-con-sonido-7-1-en-mexico" } }
        ) {
            ...ProyectoCardData
        }
        proyectoSiemens: mdx(
          fields: { slug: { eq: "/3d-mapping-siemens-aniversario-120-anos-en-mexico" } }
        ) {
            ...ProyectoCardData
        }
        proyectoTiteres: mdx(
          fields: { slug: { eq: "/instalacion-interactiva-museo-del-titere-puebla" } }
        ) {
            ...ProyectoCardData
        }
    }`
  )
  
  const pageTitle = l('📽️ Video Mapping 3D y video proyecciones avanzadas', '📽️  3D Video Projection Mapping')
  const pageImage = 'https://visualma.com' + data.headerImg.childImageSharp.resize.src

  const pageUrl = 'https://visualma.com/video-mapping';
  const pageDescription =
  l('Expertos en Video Mapping 3D y proyecciones avanzadas. Proveedores de mapping para espectáculos, eventos e instalaciones permanentes en México.',
  'Experts in Video Projection Mapping and advanced projections for events, shows and installations.')

  const MedImgProps = {
    layout: 'constrained',
    width: 800,
    height: 450,
    formats: ['auto', 'webp', 'avif'],
  }

  return (
    <Layout lang={lang}>
      <Head lang={lang}
        pageTitle={pageTitle}
        imageUrl={pageImage}
        pageDescription={pageDescription}
      />
      <VideoSchema
        videoUrl="https://www.youtube.com/embed/goiihJ5cRLI"
        contentUrl={pageUrl}
        imageUrl={pageImage}
        title={pageTitle}
        description={
          l('Ejemplos de video proyección avanzada y video mapping 3D en México por la agencia Visualma',
            'Examples of advanced video projection and 3D projection mapping created by Visualma')
        }
        date="2020-07-07T00:21:41Z"
        duration="PT52S"
      />
      <div>
        <HeroSection
          header={true}
          headerImg={data.headerImg.childImageSharp.gatsbyImageData}
          headerImgMovil={data.headerImgMovil.childImageSharp.gatsbyImageData}
          title="Video Mapping 3D"
          parrafoGrande={
            l(<>Experiencias de <b>Video y Mapping 3D</b></>,
            <><b>3D Video Projection Mapping</b> experiences</>)
          }
        />
        <Flex
          bg="transparent"
          justify="flex-start"
          direction="column"
          m="2em auto 5em"
          maxWidth="50em"
        >
          <Heading as="h2" className="subHeader" mb="0.5em">
            {l('Video Mapping 3D y Proyecciones Avanzadas', '3D Video Projection Mapping')}
            <br />
            <small>
              {l('en Escenarios y Eventos Masivos', 'in stages, buildings and massive events')}{' '}
              <span role="img" aria-label="emoji de estrella">💫</span>
            </small>
          </Heading>
        </Flex>

        <Flex>
          <video
              controls={false}
              width="100%"
              loading="lazy"
              autoPlay
              loop
              playsInline
              muted
              controlsList="nodownload"
              crossOrigin="anonymous"
              alt={l('Video mapping 3D y proyecciones avanzadas en México', 'Advanced video projection and 3D projection mapping')}
              title={l('Video mapping 3D y proyecciones avanzadas en México', 'Advanced video projection and 3D projection mapping')}
              style={{ margin: '0 auto', width: '100%', maxWidth: '100%' }}
            >
            <source src="https://player.vimeo.com/external/474137495.hd.mp4?s=e7a54ee14e1a14ca514ae9e00602cbb68bf58baf&profile_id=174"
                type="video/mp4"
              />
              Tu navegador no soporta videos.
            </video>
        </Flex>

        <Flex
          bg="transparent"
          justify="flex-start"
          direction="column"
          m="3em auto 0"
          maxWidth="50em"
        >
          <Heading as="h2" className="subHeader" mb="0em">
            {l('¿Qué es el Video Mapping 3D y para qué sirve?',
              'What is 3D video projection mapping and what is it used for?')}
          </Heading>
        </Flex>
        
            
        <p className='parrafo'>
          {l('El videomapping, también conocido como 3D mapping o projection mapping, es una técnica de video proyección que permite alinear contenido animado perfectamente sobre el mundo real creando una poderosa ilusión.', 
            'Video mapping, also known as 3D projection mapping, is a video projection technique that allows the perfect alignment of animated content to the real world creating a powerful illusion')}
        </p>

        <Flex margin="3em auto 2em" justifyContent="center" 
        >
          <Flex className="shadow-lg-white">
            <video
              controls={false}
              width="740px"
              loading="lazy"
              autoPlay
              loop
              playsInline
              muted
              controlsList="nodownload"
              crossOrigin="anonymous"
              alt={l('Video mapping 3d en edificios, escenarios y eventos masivos', 'Video projection mapping in buildings, stages and masive events')}
              title={l('Video mapping 3d en edificios y eventos masivos', 'Video projection mapping in buildings, stages and masive events')}
              style={{ margin: '0 auto', width: '520px', maxWidth: '100%' }}
            >
              <source src="https://player.vimeo.com/external/474293436.hd.mp4?s=ecc0b2c6c5352f5a050cd4603b2c350d96b021af&profile_id=175"
                type="video/mp4"
              />
              Tu navegador no soporta videos.
            </video>
          </Flex>
        </Flex>
              
        <p className='parrafo'>
          {l('Un espectáculo de video mapping logra que los objetos cobren vida, tiene un gran atractivo visual y genera un gran interes y engagement en los espectadores.',
            'A video mapping show manages to make objects become alive, has a geat visual attractive and generates great interest and engagement in the audience.')}
          <br />
          <br />
          {l('Por esta razón, se ha convertido en un recurso valioso para eventos y presentaciones de marketing a lo largo del mundo.',
            'For this reason it has become a very valuable resource for events and marketing presentations around the world.')}
          <br />
        </p>

        <Flex className="flexList flexListLeft">
          <Flex flexWrap="wrap">
            <Flex className="columna img">
              <Heading as="h3">
                {l('Mapping 3D en Edificios', '3D Mapping in Buildings')}
              </Heading>
              <Text fontSize="xl" className="parrafoChico">
                {l('Transformamos fachadas masivas en lienzos que cobran vida contando la historia de nuestros clientes y llegando a miles de espectadores.',
                  'We transform masive buildings in a canvas that turns into life telling the story of our clients project and getting through to thousands of people.')}
              </Text>
            </Flex>

            <Flex className="columna img">
              <Flex className="shadow-lg round-fun">
                <video
                  controls={false}
                  width="740px"
                  loading="lazy"
                  autoPlay
                  loop
                  playsInline
                  muted
                  controlsList="nodownload"
                  crossOrigin="anonymous"
                  alt={l('Video mapping 3d en edificios y eventos masivos', 'Video projection mapping in buildings and massive events')}
                  title={l('Video mapping 3d en edificios y eventos masivos', 'Video projection mapping in buildings and massive events')}
                  style={{ margin: '0 auto', width: '720px', maxWidth: '100%' }}
                >
                  <source src="https://player.vimeo.com/external/474137495.sd.mp4?s=0703ba95f374e1754eea69ed9c7c904e0b2b0fa2&profile_id=165"
                    type="video/mp4"
                  />
                  Tu navegador no soporta videos.
                </video>
              </Flex>
            </Flex>
          </Flex>

          <Flex flexWrap="wrap-reverse">
            <Flex className="columna img">
              <Flex className="shadow-lg round-fun">
                <video
                  controls={false}
                  width="740px"
                  loading="lazy"
                  autoPlay
                  loop
                  playsInline
                  muted
                  controlsList="nodownload"
                  crossOrigin="anonymous"
                  alt={l('Video mapping 3D en escenarios y presentaciones corporativas', 'Video projection mapping in stages and business presentations')}
                  title={l('Video mapping 3D en escenarios y presentaciones corporativas', 'Video projection mapping in stages and business presentations')}
                  style={{ margin: '0 auto', width: '720px', maxWidth: '100%' }}
                >
                  <source src="https://player.vimeo.com/external/474142385.hd.mp4?s=36d65a7da7d7e45ed35fef817608ae951ce7891f&profile_id=174"
                    type="video/mp4"
                  />
                  Tu navegador no soporta videos.
                </video>
              </Flex>
            </Flex>
          
            <Flex className="columna img">
              <Heading as="h3">
                {l('Mapping 3D en Escenarios', '3D Mapping in Stages')}
              </Heading>
              <Text fontSize="xl" className="parrafoChico">
                {l(<>Construimos <b>estructuras a la medida</b> para aprovechar al máximo los recursos del medio creando <b>lanzamientos de producto, presentaciones corporativas y exposiciónes únicas</b>.</>,
                  <>We create <b>custom build structures</b> to take advantage of everything what video projection mapping can do and achieve <b>unique product launches, business presentations and expos</b>.
                </>)}
              </Text>
            </Flex>
          </Flex>
        </Flex>

        <Flex
          bg="transparent"
          justify="flex-start"
          direction="column"
          m="3em auto"
        >
          <Heading as="h2" className="subHeader" mb="2em">
            {l('¿Dónde se puede hacer un espectáculo de videomapping?',
              'Where can i make a video projection mapping show?')}
          </Heading>
          <Flex
            flexDirection={['column', 'row']}
            maxWidth="80em"
            margin="0 auto"
            lineHeight="1.5"
            fontSize="lg"
            style={{
              paddingLeft: '1em',
              textIndent: '-1.5em',
            }}
          >
            <List
              spacing={3}
              m={['0 auto 1em', '0 auto 3em']}
              marginRight={['auto', '4em']}
              maxWidth={['280px', '350px']}
              width="100%"
            >
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                {l('Fachadas de edificios', 'Buildings')}
              </ListItem>
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                {l('Eventos públicos masivos', 'Massive public events')}
              </ListItem>
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                {l('Escenarios para lanzamientos y presentaciones', 'Product launches & presentations')}
              </ListItem>
            </List>
            <List
              spacing={3}
              m="0 auto 3em"
              maxWidth={['280px', '350px']}
              width="100%"
            >
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                {l('Centros de convenciones y expos', 'Convention centers & expos')}
              </ListItem>
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                {l('Showrooms corporativos', 'Corporate Showrooms')}
              </ListItem>
              <ListItem>
                <ListIcon as={ChevronRightIcon} color="azulin.500" />
                {l('Salas de museos interactivos', 'Interactive museums')}
              </ListItem>
            </List>
          </Flex>
          <Text fontSize="xl" className="parrafoChico spacer">
            {l('Con los video proyectores correctos, el mapping 3D puede cubrir edificios enormes con experiencias que pueden ser disfrutadas por miles de personas simultáneamente en eventos masivos, como las Olimpiadas, fiestas nacionales, conmemoraciones históricas y lanzamientos de películas.',
              'With the correct hardware, video projection mapping can cover gigant buildings with experiences that can be enjoyed by thousands of people at the same time in massive events like the Olympics, the super bowl, nacional parties, historic commemorations and film premieres.')}
            <br />
            <br />
            <Flex
              style={{
                margin: '0em auto 1em',
                width: '100%',
                maxWidth: '60em',
              }}
              flexDirection={['column', 'row']}
            >
              <Box
                className="shadow-md"
                marginRight={['0%', '2%']}
                marginBottom={['2em', '0em']}
                width={['100%', '48%']}
                loading="lazy"
                >
                <StaticImage
                  {...MedImgProps}
                  src="../../../content/images/servicios/mapping/mapping_fachada_charo_3c.jpg"
                  alt="Video mapping 3d en mexico"
                  />
              </Box>
              <Box
                className="shadow-md"
                marginLeft={['0%', '2%']}
                marginBottom={['2em', '0em']}
                width={['100%', '48%']}
                loading="lazy"
                >
                <StaticImage
                  {...MedImgProps}
                  src="../../../content/images/servicios/mapping/mapping-escenario-TNG-4c.jpg"
                  alt="Video mapping 3d en mexico"
                  />
              </Box>
            </Flex>
            <br />
            {l('Algunos de los eventos de mapping más exitosos y comunes suceden en interiores, sobre escenarios, para eventos de marketing, convenciones y lanzamientos de producto. El video mapping 3D mantiene todo su impacto sobre un escenario, y es uno de los mejores recursos para comunicar de manera memorable el mensaje de una marca o nuevo producto.',
              'Some of the most successful mapping events happen indoors for marketing events, conventions, expos and product launches. Video projection is one of the best resources to communicate the message of a brand or new product in a memorable way.')}
            <br />
          </Text>
        </Flex>

        <ContactBanner />

        <Flex className="flexList flexListLeft" borderTop="0">
          <Flex flexWrap="wrap">
            <Flex className="columna img">
              <Heading as="h2">
                {l('¿Cómo funciona el Mapping?', 'How does mapping work?')}
              </Heading>
              <Text fontSize="xl" className="parrafoChico">
                {l('El video mapping 3D, a nivel técnico, consiste en analizar la representación bidimensional de una superficie desde la perspectiva de un video proyector, y combinarla con la distorsión y perspectiva de un espectador mirando la misma superficie desde una posición distinta.',
                  'Technically 3D video projection mapping consists in the analysis of the bidimensional representation of a surface from the perspective of a video projector and mix it with the distortion and perspective of a person looking to the same surface from a different position.')}
                <br />
                <br />
                {l('Al conocer ambas perspectivas, es posible crear imágenes que parecen tener profundidad y estar alineadas con los elementos arquitectónicos de la estructura sobre la que se proyectan.',
                  'Knowing both perspectives it is possible to create images that appear to have depth and are aligned to the architectural elements of the structure on which they are projected.')}
              </Text>
            </Flex>
            <Flex className="columna full">
              <ProyectoCard
                data={data.proyectoQuinzino}
                // darkMode
              />
            </Flex>
          </Flex>
          <Flex flexWrap="wrap-reverse">
            <Flex className="columna full" padding="0">
              <ProyectoCard
                data={data.proyectoCharo}
                // darkMode
              />
            </Flex>
            <Flex className="columna img">
              <Heading as="h3">
                {l('Una ilusión Mágica y Memorable', 'A magical and memorable ilusion')}
              </Heading>
              <Text fontSize="xl" className="parrafoChico">
                {l('Cuando las imágenes proyectadas hacen parecer que la estructura real se está moviendo e interactuando con objetos virtuales se genera una poderosa ilusión visual. El espectador percibe una realidad aumentada por el contenido digital, pareciendo que la estructura cobra vida.',
                  'When the projected images make it seam as the real structure is moving and interacting with virtual objects a powerful visual illusion is created. The audience perceive a reality that is augmented by the digital content, making the structure become alive.')}
              </Text>
            </Flex>
          </Flex>
        </Flex>

        <Flex className="flexList flexListLeft" borderTop="0">
          <Flex flexWrap="wrap">
            <Flex className="columna img">
              <Heading as="h2">
                {l('¿Qué se necesita para hacer un Mapping?', 'What do i need to make a video projection mapping?')}
              </Heading>
              <Text fontSize="xl" className="parrafoChico">
                {/* Un proyecto de video mapping requiere un equipo de ingenieros y
                artistas visuales que crean contenido diseñado específicamente para
                la estructura a proyectarse. Sin un contenido adecuado, no se puede
                lograr el efecto de un espectáculo de mapping.
                <br />
                <br /> */}
                {l('Para iniciar un proyecto de mapping 3D con Visualma, basta con elegir una estructura o edificio y contar con los permisos y acceso a la locación. ',
                  'To start a video mapping project with Visualma you only have to choose a structure or building to project and have the permissions and access to the location.')}
                <br />
                <br />
                {l('Sólo es necesario compartir fotografías y/o renders con nuestro equipo de ingenieros, así como un brief narrativo donde se comunique la temática, duración de la proyección, objetivo y estilo de arte.',
                  'You only have to send us photos or renders of the structure and a narrative brief of the theme, duration, objective and art style.')}
              </Text>
            </Flex>
            <Flex className="columna full">
              <ProyectoCard
                data={data.proyectoSiemens}
                // darkMode
              />
            </Flex>
          </Flex>
          <Flex flexWrap="wrap-reverse">
            <Flex className="columna full">
              <ProyectoCard
                data={data.proyectoCholula}
              // darkMode
              // autoPlay
              />
            </Flex>
            <Flex className="columna img">
              <Heading as="h3">
                {l('Nos encargamos de todo', 'We take care of everything')}
              </Heading>
              <Text fontSize="xl" className="parrafoChico">
                <b>
                  {l('Los servicios de Visualma incluyen todo lo necesario para realizar tu proyecto de inicio a fin.',
                  'Visualma\'s services include everything necessary to realize your project from start to finish.')}
                </b>
              </Text>
              <List
                spacing={3}
                m={['0.5em auto 1em', '0.5em auto 2em']}
                marginLeft="0em"
                paddingLeft="0"
                maxWidth="100%"
                width="100%"
              >
                <ListItem>
                  <ListIcon as={ChevronRightIcon} color="azulin.500" />
                  {l('Asesoría y análisis de proyecto', 'Advisory and project analysis')}
                </ListItem>
                <ListItem>
                  <ListIcon as={ChevronRightIcon} color="azulin.500" />
                  {l('Guionado y dirección de arte', 'Scripting and art direction')}
                </ListItem>
                <ListItem>
                  <ListIcon as={ChevronRightIcon} color="azulin.500" />
                  {l('Producción de contenido animado personalizado', 'Production of personalized animated content')}
                </ListItem>
                <ListItem>
                  <ListIcon as={ChevronRightIcon} color="azulin.500" />
                  {l('Renta o compra de proyectores y servidores de proyección', 'Rent or Purchace of projectors and video servers')}
                  
                </ListItem>
                <ListItem>
                  <ListIcon as={ChevronRightIcon} color="azulin.500" />
                  {l('Calibración y montaje', 'Setup and calibration')}
                </ListItem>
                <ListItem>
                  <ListIcon as={ChevronRightIcon} color="azulin.500" />
                  {l('Operación de los equipos durante el evento', 'Operation during the event')}
                </ListItem>
              </List>
              <Text fontSize="xl" className="parrafoChico">
                {l(<>Normalmente se necesitan de <b>4 a 8 semanas</b> para desarrollar un proyecto de mapping.</>,
                  <>Normaly we need <b>4 to 8 weeks</b> to develop a video mapping project.</>)}
              </Text>
            </Flex>
          </Flex>
        </Flex>



        <Heading as="h2" className="subHeader" marginTop="3em" marginBottom="0">
          {l('¿Cómo se crea un videomapping 3D?', 'How is a 3D video projection mapping created?')}
        </Heading>
        <Flex className="flexList flexListLeft" borderTop="none" marginTop="0">
          <Flex className="columna">
            <Heading as="h3" className="subHeader">
              <span
                role="img"
                aria-label="Straight ruler"
                style={{ WebkitTextFillColor: 'white' }}
              >
                📏
              </span>{' '}
              {l('Análisis y Asesoría', 'Analysis and advisory')}
            </Heading>
            <Text fontSize="xl" className="parrafoChico">
              {l('Se inicia con un análisis de la estructura a proyectarse y sus condiciones ambientales a partir de fotografías, mediciones y planos.',
                'We start with an analysis of the structure to project and its ambient conditions through photos, measurements and blueprints.')}
              <br /><br />
              { l('Analizando los requerimientos de el proyecto, conociendo los temas a tratar y el área a proyectar, podemos comenzar el proceso.',
                'Knowing the project requirements and the subject that wants to be transmitted we can start the process.')}
            </Text>
          </Flex>

          <Flex className="columna">
            <Heading as="h3" className="subHeader">
              <span role="img" aria-label="Light bulb"
                style={{ WebkitTextFillColor: 'white' }}>
                💡
              </span>{' '}
              {l('Guionado y Diseño de arte', 'Scripting and art direction')}
            </Heading>
            <Text fontSize="xl" className="parrafoChico">
              {l('Se crea un guión que genera el contenido visual mas impactante, potencializando las características del escenario, fachada o edificio.',
                'We create the script that generates the most impactful visual experience considering the characteristics of the structure, stage or building.')}
              <br />
              <br />
              {l('Se toman en cuenta factores como la ubicación y perspectiva visual de los espectadores para maximizar los recursos narrativos.',
                'Factors like the location and visual perspective of the audience are taken into account to maximize the narrative resources.')}
            </Text>
          </Flex>

          <Flex className="columna">
            <Heading as="h3" className="subHeader">
              <span
                role="img"
                aria-label="Clapper board"
                style={{ WebkitTextFillColor: 'white' }}
              >
                🎬
              </span>{' '}
              {l('Creación de Contenido', 'Content creation')}
            </Heading>
            <Text fontSize="xl" className="parrafoChico">
              {l('El equipo de artistas audiovisuales crea un detallado modelo 3D de la estructura y da vida al guión con animaciones y modelados 3D que se mezclan con la realidad.',
                'The team of audio visual artists create a detailed 3D model from the structure and give life to the script with animation and simulations that mix with reality.')}
              <br /><br />
              {l('El contenido se crea de la mano del cliente para alinearse perfectamente con su campaña.',
                'The content is created hand in hand with the client to perfectly align the show to their campaign.')}
            </Text>
          </Flex>

          <Flex className="columna">
            <Heading as="h3" className="subHeader">
              <span
                role="img"
                aria-label="Finish flag"
                style={{ WebkitTextFillColor: 'white' }}
              >
                🎉
              </span>{' '}
              {l('Calibración, Ejecución y Producción', 'Calibration, Execution and Projection')}
            </Heading>
            <Box fontSize="xl" className="parrafoChico">
              {l('Nos aseguramos de que tu espectáculo funcione a la perfección.', 
                'We make sure that your show works perfectly.')}
              <br /><br />
               {l('Montaje en locación, proceso de calibración, técnicos, hardware y producción.', 
                  'On-site setup, calibration process, technicians and production.')}
              <br /><br />
              {l('Los servicios de Visualma incluyen todo lo necesario para realizar tu proyecto de inicio a fin.', 
                'Visualma\'s services include everything necesary to realize your project from start to finish.')}
            </Box>
          </Flex>
            


        </Flex>

        <Flex className="flexList flexListLeft" borderTop="0">
          <Flex flexWrap="wrap">
            <Flex className="columna img">
              <Heading as="h2">
                {l('¿Cuánto cuesta un VideoMapping?',
                  'What is the cost of a Video Projection Mapping?')}
              </Heading>
              <Text fontSize="xl" className="parrafoChico">
              {/* Un proyecto de video mapping requiere un equipo de ingenieros y
              artistas visuales que crean contenido diseñado específicamente para
              la estructura a proyectarse. Sin un contenido adecuado, no se puede
              lograr el efecto de un espectáculo de mapping.
              <br />
              <br /> */}
                {l('El precio de un video mapping varía de proyecto a proyecto, ya que cada estructura y evento tiene necesidades distintas.',
                  'The price of a video mapping is different for every project because every structure and event has its own necessities.')}
              <br />
              <br />
                {l('Dos factores principales determinan el costo de un mapping 3D: El tamaño del área a proyectarse y la duración del contenido a producirse. Un área mayor requiere de la renta de más video proyectores de mapping. Un espectáculo más largo requerirá una mayor cantidad de horas de desarrollo por parte de los artistas.',
                  'Two main factor that determine the cost of a 3D mapping are: The size of the area that will be projected and the duration of the content that will be produced. A bigger area requires the rent of more video projectors. A longer show needs more hours of development from the artists.')}
            
            </Text>
          </Flex>
          <Flex className="columna full">
            <ProyectoCard
              data={data.proyectoXx}
            // darkMode
            // autoPlay
            />
          </Flex>
        </Flex>

        <Flex flexWrap="wrap-reverse">
          <Flex className="columna full">
            <ProyectoCard
              data={data.proyectoBalon}
            // darkMode
            // autoPlay
            />
          </Flex>
          <Flex className="columna img">
            <Heading as="h2">
              {l('Optimización de costos', 'Cost optimization')}
            </Heading>
            <Text fontSize="xl" className="parrafoChico">
              {l('Es posible optimizar el costo de un evento de mapping seleccionando los mejores equipos de proyección para cada situación, definiendo el área a proyectar y usando los recursos disponibles de la manera mas optima. ',
                'It is possible to optimize the cost of a video mapping event selecting the best hardware for each situation, defining the correct projection area and using the available resources in the most optimum metter')}
              <br /><br />
              {l('Un proyecto de mapping profesionalmente planificado puede entrar en el presupuesto de cualquier tipo de evento.',
                'A mapping that is correctly planned can enter the budget of any kind of event.')}
            </Text>
          </Flex>
        </Flex>
      </Flex>

        <Flex className="flexList flexListLeft" borderTop="0">
          <Flex flexWrap="wrap">
            <Flex className="columna img">
              <Heading as="h3">
                {l('Mapping Interactivo', 'Interactive Mapping')}
              </Heading>
              <Text fontSize="xl" className="parrafoChico">
                {l('Utilizamos distintos tipos de sensores para permitir a espectadores, artistas o presentadores influir y transformar el contenido del mapping en tiempo real.',
                  'We use different types of sensors to allow the audience, artists and hosts to transform the content of the video mapping in real time.')}
              </Text>
            </Flex>
            <Flex className="columna full">
              <ProyectoCard
                data={data.proyectoTiteres}
                //darkMode
              />
            </Flex>
          </Flex>

          <Flex flexWrap="wrap-reverse">
            <Flex className="columna full">
              <ProyectoCard
                data={data.proyectoEpura}
                // darkMode
              />
            </Flex>
            <Flex className="columna img">
              <Heading as="h3">
                {l('Hologramas', 'Holograms')}
              </Heading>
              <Text fontSize="xl" className="parrafoChico">
                {l('Realizamos proyecciones holograficas de objetos que parecen flotar sobre el espacio y logran la ilusión de un holograma de ciencia ficción.',
                  'We create holographic projections where objects and characters seam to float in the room giving the illusion of a science fiction hologram.')}
              </Text>
            </Flex>
          </Flex>
        </Flex>

        <ContactBanner lang={lang}/>

        <Proyectos lang={lang}
          title={l('Proyectos de Mapping 3D', '3D Projection Mapping examples')}
          category="Video Mapping 3D"
          limit={15}
        />
      </div>
    </Layout>
  );
};
Mapping.propTypes = {
  lang: PropTypes.string
};

export default Mapping;
